import PropTypes from 'prop-types';
import { useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import Heading from 'shopper/components/Heading';

import CarouselBox from 'components/CarouselBox';
import OfferCard from 'components/Timeline/OfferCard/V1/OfferCard';

import useIntersectionObserver from 'hooks/useIntersectionObserver';

import { sendEvent } from 'lib/gtag';

const CAROUSEL_MIN_LENGTH = 4;

const OffersCarousel = ({
  className,
  gaEventCategory,
  gaEventActionPrefix = null,
  offers,
  offerCardProps = null,
  headingProps = {
    children: 'Confira essas ofertas selecionadas',
  },
  ...rest
}) => {
  const offerCarouselRef = useRef(null);

  useIntersectionObserver({
    target: offerCarouselRef,
    threshold: 0,
    onIntersect: () => {
      sendEvent({ action: 'carousel_show', category: gaEventCategory });
    },
  });

  if (offers.length < CAROUSEL_MIN_LENGTH) {
    return null;
  }

  return (
    <div
      ref={offerCarouselRef}
      className={twMerge('mb-6 px-2 lg:px-0', className)}
      {...rest}
    >
      <Heading as="h5" className="mb-4" size="size5" {...headingProps} />
      {offers && (
        <CarouselBox
          arrowsProps={{
            className: 'none md:flex',
            offsetX: { left: '-left-[10px]', right: '-right-[10px]' },
          }}
          carouselProps={{
            className: 'w-full',
          }}
          itemProps={{
            breakpoints: {
              md: { slidesPerView: 3 },
              lg: { slidesPerView: 4 },
            },
            itemSize: 88,
            slidesPerView: 1,
            snapMode: 'snap-center',
          }}
          list={offers}
          render={(offer) => (
            <OfferCard
              gaEventActionPrefix={gaEventActionPrefix}
              gaEventCategory={gaEventCategory}
              {...offerCardProps}
              {...offer}
            />
          )}
          showArrows
        />
      )}
    </div>
  );
};

OffersCarousel.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventActionPrefix: PropTypes.string,
  gaEventCategory: PropTypes.string.isRequired,
  offers: PropTypes.arrayOf(
    PropTypes.shape({
      offerId: PropTypes.number.isRequired,
      offerTitle: PropTypes.string.isRequired,
      offerSlug: PropTypes.string.isRequired,
      offerPhoto: PropTypes.string.isRequired,
      offerPrice: PropTypes.number.isRequired,
      offerOldPrice: PropTypes.number,
      offerPriceType: PropTypes.string.isRequired,
      offerPublished: PropTypes.string.isRequired,
      offerIsHighlight: PropTypes.bool.isRequired,
      ratings: PropTypes.shape().isRequired,
      offerStatusName: PropTypes.string.isRequired,
      offerTags: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          type: PropTypes.string.isRequired,
        })
      ),
      offerComments: PropTypes.number.isRequired,
      storeDomain: PropTypes.string.isRequired,
    })
  ).isRequired,
  headingProps: PropTypes.shape(),
  offerCardProps: PropTypes.shape(),
};

export default OffersCarousel;
